import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { debounce, isEmpty } from 'underscore';
import classNames from 'classnames';
import { Map } from 'immutable';
import moment from 'moment';
import { push } from 'connected-react18-router';

import { downloadBrowseFile } from 'src/utils/downloadFile';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import * as browseActionCreators from '../../actions/browse';
import { BrowseInputRange } from '../../components/Browse/BrowseInputRange';
import BrowseInputTag from '../../components/Browse/BrowseInputTag';
import { BrowseAutoCompleteTag } from '../../components/Browse/BrowseAutoCompleteTag';
import { BrowseStateFilter } from '../../components/Browse/BrowseStateFilter';
import BrowseCountryFilter from '../../components/Browse/BrowseCountryFilter';
import BrowseBuyerFilter from '../../components/Browse/BrowseBuyerFilter';
import { BrowseActionFilter } from '../../components/Browse/BrowseActionFilter';
import { BrowseDropDown } from '../../components/Browse/BrowseDropDown';
import BrowseTable from '../../components/Browse/BrowseTable';
import { BrowseEmployeeInputRange } from '../../components/Browse/BrowseEmployeeInputRange';
import { BrowseUploadSearch } from '../../components/Browse/BrowseUploadSearch';
import config from '../../config';
import {
  buildRangeParam,
  buildDateParam,
  buildTagParam,
  buildCheckParam,
  buildActionParam,
  buildSortByParam,
  buildDropdownParam,
  buildActivityParam,
  buildUploadParam,
  buildTagObjectParam,
} from '../../helpers/paramBuilder';
import { fetchTargetStatuses } from '../../actions/statuses';
import { fetchEventActivities } from '../../actions/eventActivities';
import { BrowseStatusesFilter } from '../../components/Browse/BrowseStatusesFilter';
import { BrowseActivityFilter } from '../../components/Browse/BrowseActivityFilter';
import { showError } from '../../utils/MessagePopup';
import { checkBrowsePageAccess } from '../../utils/checkPermissions';
import { BrowseCheckbox } from '../../components/Browse/BrowseCheckbox';
import { DirectorDashboardFilter } from '../../components/Browse/DirectorDashboardFilter';
import { usePopupContext } from '../../contexts/PopupContext';
import { Card } from '../../components/helpers/Card';

import style from './style.module.css';

const SAFE_EXPORT_TARGETS_NUMBER = 300;
const companyTypes = config.values.get('companyTypes');
const targetsFilter = config.browse.getIn(['filter', 'targets']);
const columnDefs = config.browse.getIn(['table', 'columnDefs', 'targets']);

const NUMBER = /[0-9]/g;

/**
 * Prepare moment() date object into ISO string.
 *
 * @param {object} data Action data.
 */
const prepareActionData = data => ({
  ...data,
  endDate: moment.isMoment(data.endDate) ? data.endDate.toISOString() : data.endDate,
  startDate: moment.isMoment(data.startDate) ? data.startDate.toISOString() : data.startDate,
});

/**
 * Build all state data to restore it on a getting client.
 *
 * @param {object} filterState Targets filter state.
 */
const buildFiltersStateParam = filterState => {
  const stateParams = {};

  // Collect needed filters if it's selected.
  if (filterState.getIn(['currentStatus', 'checked']))
    stateParams.currentStatus = filterState.get('currentStatus').toJS();
  if (filterState.getIn(['highStatus', 'checked'])) stateParams.highStatus = filterState.get('highStatus').toJS();
  if (filterState.getIn(['revenue', 'checked'])) stateParams.revenue = filterState.get('revenue').toJS();
  if (filterState.getIn(['ebitda', 'checked'])) stateParams.ebitda = filterState.get('ebitda').toJS();
  if (filterState.getIn(['employees', 'checked'])) stateParams.employees = filterState.get('employees').toJS();
  if (filterState.getIn(['states', 'checked'])) stateParams.states = filterState.get('states').toJS();
  if (filterState.getIn(['country', 'checked'])) stateParams.country = filterState.get('country').toJS();
  if (filterState.getIn(['module', 'checked'])) stateParams.module = filterState.get('module').toJS();
  if (filterState.getIn(['analyst', 'checked'])) stateParams.analyst = filterState.get('analyst').toJS();
  if (filterState.getIn(['dealMaker2', 'checked'])) stateParams.dealMaker2 = filterState.get('dealMaker2').toJS();
  if (filterState.getIn(['researcher', 'checked'])) stateParams.researcher = filterState.get('researcher').toJS();
  if (filterState.getIn(['buyer', 'checked'])) stateParams.buyer = filterState.get('buyer').toJS();
  if (filterState.get('isNewProfile')) stateParams.isNewProfile = filterState.get('isNewProfile');
  if (filterState.get('isUpdatedProfile')) stateParams.isUpdatedProfile = filterState.get('isUpdatedProfile');
  if (filterState.get('isConfirmedProfile')) stateParams.isConfirmedProfile = filterState.get('isConfirmedProfile');
  if (filterState.getIn(['harvcoTags', 'checked'])) stateParams.harvcoTags = filterState.get('harvcoTags').toJS();
  if (filterState.getIn(['industry', 'checked'])) stateParams.industry = filterState.get('industry').toJS();
  if (filterState.getIn(['mergeResultOnly', 'checked']))
    stateParams.mergeResultOnly = prepareActionData(filterState.get('mergeResultOnly').toJS());
  if (filterState.getIn(['directorDashboard', 'checked']))
    stateParams.directorDashboard = filterState.get('directorDashboard').toJS();
  if (filterState.getIn(['nextAction', 'checked']))
    stateParams.nextAction = prepareActionData(filterState.get('nextAction').toJS());
  if (filterState.getIn(['lastAction', 'checked']))
    stateParams.lastAction = prepareActionData(filterState.get('lastAction').toJS());

  if (filterState.getIn(['description', 'checked'])) stateParams.description = filterState.get('description').toJS();
  if (filterState.getIn(['companyType', 'checked'])) stateParams.companyType = filterState.get('companyType').toJS();
  if (filterState.getIn(['areaCode', 'checked'])) stateParams.areaCode = filterState.get('areaCode').toJS();
  if (filterState.getIn(['keywords', 'checked'])) stateParams.keywords = filterState.get('keywords').toJS();
  if (filterState.getIn(['projectType', 'checked'])) stateParams.projectType = filterState.get('projectType').toJS();
  if (filterState.getIn(['uploadSearch', 'checked'])) stateParams.uploadSearch = filterState.get('uploadSearch').toJS();

  return stateParams;
};

const buildRequestParam = filterState => {
  const requestParams = {
    ...buildRangeParam(filterState, 'current_status_range', 'currentStatus'),
    ...buildRangeParam(filterState, 'high_status_range', 'highStatus'),
    ...buildRangeParam(filterState, 'revenue_range', 'revenue'),
    ...buildRangeParam(filterState, 'ebitda_range', 'ebitda'),
    ...buildRangeParam(filterState, 'employee_number_range', 'employees'),
    ...buildTagParam(filterState, 'states', 'states', 'value'),
    ...buildTagParam(filterState, 'countries', 'country', 'id'),
    ...buildTagParam(filterState, 'directors', 'module', 'id'),
    ...buildTagParam(filterState, 'analysts', 'analyst', 'id'),
    ...buildTagParam(filterState, 'deal_makers_2', 'dealMaker2', 'id'),
    ...buildTagParam(filterState, 'researchers', 'researcher', 'id'),
    ...buildCheckParam(filterState, 'is_new_profile', 'isNewProfile'),
    ...buildCheckParam(filterState, 'is_updated_profile', 'isUpdatedProfile'),
    ...buildCheckParam(filterState, 'is_confirmed_profile', 'isConfirmedProfile'),
    ...buildTagParam(filterState, 'tags', 'harvcoTags', 'value'),
    ...buildTagParam(filterState, 'industries', 'industry', 'value'),
    ...buildDateParam(filterState, 'next_action_dates', 'nextAction'),
    ...buildActionParam(filterState, 'next_actions', 'nextAction'),
    ...buildTagParam(filterState, 'descriptions', 'description', 'value'),
    ...buildTagParam(filterState, 'company_types', 'companyType', 'value'),
    ...buildTagParam(filterState, 'area_codes', 'areaCode', 'value'),
    ...buildTagParam(filterState, 'keywords', 'keywords', 'value'),
    ...buildDropdownParam(filterState, 'project_type', 'projectType'),
    ...buildUploadParam(filterState, 'upload_search', 'uploadSearch'),
  };

  if (filterState.getIn(['lastAction', 'lastActivity'])) {
    Object.assign(requestParams, buildDateParam(filterState, 'last_action_dates', 'lastAction'));
    Object.assign(requestParams, buildTagObjectParam(filterState, 'last_actions', 'lastAction'));
  } else {
    Object.assign(requestParams, buildActivityParam(filterState, 'activity', 'lastAction'));
  }

  if (Array.isArray(requestParams.activity)) {
    const activityUsers = buildTagParam(filterState, 'users', 'lastAction.users', 'id');
    const activityBuyers = buildTagParam(filterState, 'buyers', 'lastAction.buyers', 'id');
    const eventActivities = buildTagObjectParam(filterState, 'activities', 'lastAction', 'id');

    if (activityUsers) {
      requestParams.activity.push({
        users: activityUsers.users,
      });
    }

    if (activityBuyers) {
      requestParams.activity.push({
        buyers: activityBuyers.buyers,
      });
    }

    if (eventActivities) {
      requestParams.activity.forEach(activity => {
        if (activity.activities) {
          // eslint-disable-next-line no-param-reassign
          activity.activities = eventActivities.activities;
        }
      });
    }
  }

  if (filterState.getIn(['projectType', 'checked'])) {
    requestParams.project_type = filterState.getIn(['projectType', 'selected', 'value']);
  }

  if (filterState.getIn(['mergeResultOnly', 'checked'])) {
    requestParams.merge_result_only = filterState.getIn(['mergeResultOnly', 'checked']);
  }

  if (filterState.getIn(['directorDashboard', 'checked'])) {
    const reportType = filterState.getIn(['directorDashboard', 'reportType']);
    const data = filterState.getIn(['directorDashboard', 'data']);

    requestParams.directorDashboard = {
      ...requestParams.directorDashboard,
      [reportType]: {
        ...data.toJS(),
      },
    };
  }

  if (filterState.getIn(['lastAction', 'projectType', 'checked'])) {
    if (Array.isArray(requestParams.activity)) {
      const { activity } = requestParams;

      activity.push({
        project_type: filterState.getIn(['lastAction', 'projectType', 'selected', 'value']),
      });
      Object.assign(requestParams, { activity });
    }
  }

  if (filterState.getIn(['lastAction', 'statusChanged'])) {
    if (Array.isArray(requestParams.activity)) {
      const statusChangedFromValues = filterState.getIn(['lastAction', 'statusChangedFrom', 'text']).toJS();
      const statusChangedToValues = filterState.getIn(['lastAction', 'statusChangedTo', 'text']).toJS();
      const activity = requestParams.activity.slice();

      activity.push({
        status_changed_from: {
          min: statusChangedFromValues.min,
          max: statusChangedFromValues.max,
        },
      });
      activity.push({
        status_changed_to: {
          min: statusChangedToValues.min,
          max: statusChangedToValues.max,
        },
      });
      Object.assign(requestParams, { activity });
    }
  }

  if (filterState.getIn(['buyer', 'checked'])) {
    const selectedList = filterState.getIn(['buyer', 'selectedList']).toJS();
    const selectedListParam = filterState.getIn(['buyer', 'selectedListParam']).toJS();

    const includedBuyers = selectedListParam.filter((item, i) => !selectedList[i].exclude);
    const excludedBuyers = selectedListParam.filter((item, i) => selectedList[i].exclude);

    if (selectedListParam.length > 0) {
      requestParams.buyers = {
        included: includedBuyers,
        excluded: excludedBuyers,
      };
    }
  }

  if (filterState.getIn(['states', 'checked'])) {
    const selectedList = filterState.getIn(['states', 'selectedList']).toJS();

    if (selectedList.length > 0) {
      requestParams.states = selectedList.map(item => ({
        state: {
          included: item.state.exclude ? [] : [item.state.include],
          excluded: !item.state.exclude ? [] : [item.state.include],
        },
        cities: {
          included: item.cities
            .filter(city => Boolean(city.include))
            .filter(city => !city.exclude)
            .map(city => city.include),
          excluded: item.cities.filter(city => city.exclude).map(city => city.include),
        },
      }));
    }
  }

  return requestParams;
};

export const BrowseTargets = props => {
  const {
    activities,
    allActivitiesWithoutDuplicates,
    checkExportAvailability,
    handleAbortRequest,
    handleBrowse,
    handleClearFilter,
    handleConfirmMerging,
    handleExportWithSearchCriteria,
    handleFetchCriteria,
    handleGetBuyerApprovalSuggestion,
    handleGetBuyerNameSuggestion,
    handleGetBuyerProjectSuggestion,
    handleGetBuyerSuggestion,
    handleGetCitySuggestion,
    handleGetCompanyStateSuggestion,
    handleGetModuleSuggestion,
    handleGetUsersSuggestion,
    handleSaveAddUpdateBuyer,
    handleSearchParamsBeforeBrowse,
    handleTargetDelete,
    handleTargetDeleteConfirmation,
    handleTargetPushNextActionsDates,
    handleTargetSetNextActions,
    handleTargetsAddTag,
    handleUpdateFilter,
    handleUpdateQueryResult,
    handleUpdateTargetsActivityFilter,
    handleUpdateTargetsBuyerFilter,
    handleUpdateTargetsStateFilter,
    location,
    massSelectRows,
    onFetchEventActivities,
    onFetchStatuses,
    push,
    setSearchParamsHash,
    statuses,
    targets,
    updateTabVisibility,
    user,
    ...rest
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const keepSelectionRef = useRef(false);
  const hasMountedRef = useRef(false);
  const previousTargetsRef = useRef(targets);
  // Bundles every prop a debounced handler reads live (rather than one ref per value), per
  // [[feedback-minimize-useref]] - debounced handlers can't use the re-subscription trick since
  // that would reset their debounce timers, so a ref is genuinely unavoidable here.
  const liveRef = useRef({ targets, activities, allActivitiesWithoutDuplicates });

  liveRef.current = { targets, activities, allActivitiesWithoutDuplicates };

  const hasPageAccess = checkBrowsePageAccess(user, 'targets');

  const handleChange = useCallback(
    data => {
      if (data.filterName === 'sortBy') {
        keepSelectionRef.current = true;
      }

      const extra = data.key ? { key: data.key } : {};

      handleUpdateFilter({
        ...extra,
        name: 'targets',
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [handleUpdateFilter],
  );

  const handleStateFilterChange = useCallback(
    data => {
      handleUpdateTargetsStateFilter(data);
    },
    [handleUpdateTargetsStateFilter],
  );

  const handleGetCompanyCitySuggestion = useCallback(
    (state, value) => {
      handleGetCitySuggestion('targets', 'target', state, value, 1);
    },
    [handleGetCitySuggestion],
  );

  const handleGetCountriesByContinent = useCallback(
    (value, event) => {
      const { checked } = event.currentTarget;
      const country = targets.get('country');
      const allCountriesSuggestions = targets.get('allCountriesSuggestions');
      const existingSuggestions = country.get('selectedList');

      let newSuggestions;

      if (checked) {
        const checkedCountries = allCountriesSuggestions
          .filter(suggestion => suggestion.continentId === value)
          .map(suggestion =>
            Map({
              include: true,
              id: suggestion.id,
              text: suggestion.text,
              continentId: value,
            }),
          );

        newSuggestions = existingSuggestions.push(...checkedCountries);
      } else {
        newSuggestions = country.get('selectedList').filter(suggestion => suggestion.get('continentId') !== value);
      }

      handleChange({
        filterName: 'country',
        filterData: {
          selectedList: newSuggestions,
        },
      });
    },
    [targets, handleChange],
  );

  const fetchSuggestionsForModule = useCallback(
    ({ value }) => {
      handleGetModuleSuggestion('targets', value);
    },
    [handleGetModuleSuggestion],
  );
  const handleGetSuggestionForModule = useMemo(() => debounce(fetchSuggestionsForModule, 200), [
    fetchSuggestionsForModule,
  ]);

  const fetchNextSuggestionsForModule = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['module', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetModuleSuggestion('targets', value, currentPage + 1);
      }
    },
    [handleGetModuleSuggestion],
  );
  const handleGetNextSuggestionForModule = useMemo(() => debounce(fetchNextSuggestionsForModule, 200), [
    fetchNextSuggestionsForModule,
  ]);

  const fetchSuggestionsForAnalyst = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('targets', 'analyst', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForAnalyst = useMemo(() => debounce(fetchSuggestionsForAnalyst, 200), [
    fetchSuggestionsForAnalyst,
  ]);

  const fetchNextSuggestionsForAnalyst = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['analyst', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('targets', 'analyst', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForAnalyst = useMemo(() => debounce(fetchNextSuggestionsForAnalyst, 200), [
    fetchNextSuggestionsForAnalyst,
  ]);

  const fetchSuggestionsForDealMaker = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('targets', 'dealMaker2', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForDealMaker = useMemo(() => debounce(fetchSuggestionsForDealMaker, 200), [
    fetchSuggestionsForDealMaker,
  ]);

  const fetchNextSuggestionsForDealMaker = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['dealMaker2', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('targets', 'dealMaker2', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForDealMaker = useMemo(() => debounce(fetchNextSuggestionsForDealMaker, 200), [
    fetchNextSuggestionsForDealMaker,
  ]);

  const fetchSuggestionsForResearcher = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('targets', 'researcher', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForResearcher = useMemo(() => debounce(fetchSuggestionsForResearcher, 200), [
    fetchSuggestionsForResearcher,
  ]);

  const fetchSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('targets.lastAction', 'users', value, 1, true);
    },
    [handleGetUsersSuggestion],
  );
  const handleGetSuggestionForActivityUser = useMemo(() => debounce(fetchSuggestionsForActivityUser, 200), [
    fetchSuggestionsForActivityUser,
  ]);

  const fetchSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      handleGetBuyerSuggestion('targets.lastAction', 'buyers', value, 1, true);
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetSuggestionForActivityBuyer = useMemo(() => debounce(fetchSuggestionsForActivityBuyer, 200), [
    fetchSuggestionsForActivityBuyer,
  ]);

  const fetchNextSuggestionsForResearcher = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['researcher', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('targets', 'researcher', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForResearcher = useMemo(() => debounce(fetchNextSuggestionsForResearcher, 200), [
    fetchNextSuggestionsForResearcher,
  ]);

  const fetchNextSuggestionsForActivityUser = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['lastAction', 'users', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('targets.lastAction', 'users', value, currentPage + 1, true);
      }
    },
    [handleGetUsersSuggestion],
  );
  const handleGetNextSuggestionForActivityUser = useMemo(
    () => debounce(fetchNextSuggestionsForActivityUser, 200),
    [fetchNextSuggestionsForActivityUser],
  );

  const fetchNextSuggestionsForActivityBuyer = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['lastAction', 'buyers', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerSuggestion('targets.lastAction', 'buyers', value, currentPage + 1, true);
      }
    },
    [handleGetBuyerSuggestion],
  );
  const handleGetNextSuggestionForActivityBuyer = useMemo(
    () => debounce(fetchNextSuggestionsForActivityBuyer, 200),
    [fetchNextSuggestionsForActivityBuyer],
  );

  const fetchBuyerSuggestionsForBuyer = useCallback(
    ({ value }) => {
      handleGetBuyerNameSuggestion(value);
    },
    [handleGetBuyerNameSuggestion],
  );
  const handleGetBuyerSuggestionForBuyer = useMemo(() => debounce(fetchBuyerSuggestionsForBuyer, 200), [
    fetchBuyerSuggestionsForBuyer,
  ]);

  const fetchNextBuyerSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const info = liveRef.current.targets.getIn(['buyer', 'buyer', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerNameSuggestion(value, currentPage + 1);
      }
    },
    [handleGetBuyerNameSuggestion],
  );
  const handleGetNextBuyerSuggestionForBuyer = useMemo(() => debounce(fetchNextBuyerSuggestionsForBuyer, 200), [
    fetchNextBuyerSuggestionsForBuyer,
  ]);

  const fetchProjectSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const buyerSelected = liveRef.current.targets.getIn(['buyer', 'buyer', 'selected']);

      if (buyerSelected) {
        handleGetBuyerProjectSuggestion(buyerSelected.get('id'), value);
      }
    },
    [handleGetBuyerProjectSuggestion],
  );
  const handleGetProjectSuggestionForBuyer = useMemo(() => debounce(fetchProjectSuggestionsForBuyer, 200), [
    fetchProjectSuggestionsForBuyer,
  ]);

  const fetchNextProjectSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const buyerSelected = liveRef.current.targets.getIn(['buyer', 'buyer', 'selected']);
      const info = liveRef.current.targets.getIn(['buyer', 'project', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (buyerSelected && currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerProjectSuggestion(buyerSelected.get('id'), value, currentPage + 1);
      }
    },
    [handleGetBuyerProjectSuggestion],
  );
  const handleGetNextProjectSuggestionForBuyer = useMemo(
    () => debounce(fetchNextProjectSuggestionsForBuyer, 200),
    [fetchNextProjectSuggestionsForBuyer],
  );

  const fetchApprovalSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const projectSelected = liveRef.current.targets.getIn(['buyer', 'project', 'selected']);

      if (projectSelected) {
        handleGetBuyerApprovalSuggestion(projectSelected.get('id'), value);
      }
    },
    [handleGetBuyerApprovalSuggestion],
  );
  const handleGetApprovalSuggestionForBuyer = useMemo(() => debounce(fetchApprovalSuggestionsForBuyer, 200), [
    fetchApprovalSuggestionsForBuyer,
  ]);

  const fetchNextApprovalSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const projectSelected = liveRef.current.targets.getIn(['buyer', 'project', 'selected']);
      const info = liveRef.current.targets.getIn(['buyer', 'approval', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (projectSelected && currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerApprovalSuggestion(projectSelected.get('id'), value, currentPage + 1);
      }
    },
    [handleGetBuyerApprovalSuggestion],
  );
  const handleGetNextApprovalSuggestionForBuyer = useMemo(
    () => debounce(fetchNextApprovalSuggestionsForBuyer, 200),
    [fetchNextApprovalSuggestionsForBuyer],
  );

  const fetchPrioritySuggestionsForBuyer = useCallback(
    ({ value }) => {
      const suggestions = [
        { value: 'A', name: 'A' },
        { value: 'B', name: 'B' },
        { value: 'C', name: 'C' },
      ];

      if (value === '') {
        handleUpdateTargetsBuyerFilter({
          filterName: 'priority',
          filterData: {
            suggestions,
          },
        });
      }
    },
    [handleUpdateTargetsBuyerFilter],
  );
  const handleGetPrioritySuggestionForBuyer = useMemo(() => debounce(fetchPrioritySuggestionsForBuyer, 200), [
    fetchPrioritySuggestionsForBuyer,
  ]);

  const fetchSuggestionsForNextAction = useCallback(
    ({ value }) => {
      const suggestions = liveRef.current.allActivitiesWithoutDuplicates.filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'targets',
        filterName: 'nextAction',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForNextAction = useMemo(() => debounce(fetchSuggestionsForNextAction, 200), [
    fetchSuggestionsForNextAction,
  ]);

  const fetchSuggestionsForLastAction = useCallback(
    ({ value }) => {
      const suggestions = liveRef.current.activities?.filter(
        activity =>
          activity
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'targets',
        filterName: 'lastAction',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForLastAction = useMemo(() => debounce(fetchSuggestionsForLastAction, 200), [
    fetchSuggestionsForLastAction,
  ]);

  const fetchSuggestionsForCompanyType = useCallback(
    ({ value }) => {
      const suggestions = companyTypes.filter(
        companyType =>
          companyType
            .get('value')
            .toLowerCase()
            .indexOf(value.toLowerCase()) > -1,
      );

      handleUpdateFilter({
        name: 'targets',
        filterName: 'companyType',
        filterData: {
          suggestions,
        },
      });
    },
    [handleUpdateFilter],
  );
  const handleGetSuggestionForCompanyType = useMemo(() => debounce(fetchSuggestionsForCompanyType, 200), [
    fetchSuggestionsForCompanyType,
  ]);

  const handleSubFilterChange = useCallback(
    data => {
      handleUpdateTargetsBuyerFilter(data);
    },
    [handleUpdateTargetsBuyerFilter],
  );

  const handleSubActivityFilterChange = useCallback(
    data => {
      handleUpdateTargetsActivityFilter(data);
    },
    [handleUpdateTargetsActivityFilter],
  );

  const handleGetNextPageData = useCallback(
    (page, keepSelection = true) => {
      const params = buildRequestParam(targets);

      if (!isEmpty(params)) {
        const sortBy = buildSortByParam(targets);

        handleSearchParamsBeforeBrowse({
          params: {
            requestParams: params,
            stateParams: buildFiltersStateParam(targets),
          },
          name: 'targets',
          afterSuccess: () => {
            handleBrowse({
              name: 'targets',
              params,
              page,
              sortBy: sortBy.replace(NUMBER, char => `_${char}`),
              keepSelection,
            });
          },
        });
      }
    },
    [targets, handleSearchParamsBeforeBrowse, handleBrowse],
  );

  const handleDownloadExport = useCallback(
    (idList, params) => {
      const url = `${config.API_BASE_URL}/api/v1/browse/targets/export`;

      const afterSuccess = () => {
        if (idList.length > SAFE_EXPORT_TARGETS_NUMBER) {
          handleExportWithSearchCriteria(url, idList, params);
        } else {
          downloadBrowseFile(url, {
            selected_ids: idList,
            hash: params.hash,
          });
        }
      };

      checkExportAvailability({
        params: {
          selected_ids: idList,
        },
        callback: {
          afterSuccess,
          afterError: ({ responseBody }) => {
            if (responseBody) {
              showError(openPopup, [responseBody?.message || 'An error occurred'], closePopup());
            }
          },
        },
      });
    },
    [checkExportAvailability, handleExportWithSearchCriteria, openPopup, closePopup],
  );

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/company/${row.data.id}`, '_blank');
  }, []);

  const handleToggle = useCallback(() => {
    updateTabVisibility({
      field: 'targets.showTabControls',
      value: !targets.get('showTabControls'),
    });
  }, [targets, updateTabVisibility]);

  const handleGridReady = useCallback(() => {}, []);

  const handleFilterClear = useCallback(
    name => {
      handleClearFilter({ name });
    },
    [handleClearFilter],
  );

  const handleRequestAbort = useCallback(
    name => {
      handleAbortRequest(name);
    },
    [handleAbortRequest],
  );

  useEffect(() => {
    onFetchStatuses();
    onFetchEventActivities();

    const searchParams = new URLSearchParams(location.search);
    const researcher = searchParams.get('researcher');
    const criteria = searchParams.get('criteria');

    if (researcher) {
      handleChange({
        filterName: 'researcher',
        filterData: {
          checked: true,
        },
      });
    }

    if (criteria) {
      const payload = {
        name: 'targets',
        hash: criteria,
      };

      handleFetchCriteria(payload);
      setSearchParamsHash(payload);
    }

    if (!hasPageAccess) {
      showError(openPopup, config.permisionError, () => {
        closePopup();
      });
    }
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  // Combines the original UNSAFE_componentWillReceiveProps (sort-model change) and
  // componentDidUpdate (researcher URL param) logic - both compare "previous" vs "current"
  // targets and neither fires on mount, so they share one hasMountedRef guard and one
  // previousTargetsRef snapshot per [[feedback-minimize-useref]].
  useEffect(() => {
    if (hasMountedRef.current) {
      const previousTargets = previousTargetsRef.current;
      const newSortModel = targets.getIn(['sortBy', 'sortModel']);
      const previousSortModel = previousTargets.getIn(['sortBy', 'sortModel']);

      if (newSortModel !== previousSortModel) {
        const requestParam = buildRequestParam(targets);

        if (!isEmpty(requestParam)) {
          const sortBy = buildSortByParam(targets);

          handleBrowse({
            name: 'targets',
            params: requestParam,
            page: 1,
            sortBy: sortBy.replace(NUMBER, char => `_${char}`),
            keepSelection: keepSelectionRef.current,
          });
          keepSelectionRef.current = false;
        }
      }

      const searchParams = new URLSearchParams(location.search);
      const researcher = searchParams.get('researcher');
      const userId = searchParams.get('userId');

      if (researcher && userId) {
        if (!previousTargets.getIn(['researcher', 'checked']) && targets.getIn(['researcher', 'checked'])) {
          handleGetSuggestionForResearcher({ value: researcher });
        }

        if (
          previousTargets.getIn(['researcher', 'suggestions']).size !== 1 &&
          targets.getIn(['researcher', 'suggestions']).size >= 1
        ) {
          handleChange({
            filterName: 'researcher',
            filterData: {
              selectedList: [
                targets.getIn(['researcher', 'suggestions']).find(suggestion => suggestion.id === Number(userId)),
              ],
              suggestions: [],
            },
          });
        }

        if (
          previousTargets.getIn(['researcher', 'selectedList']).size === 0 &&
          targets.getIn(['researcher', 'selectedList']).size === 1
        ) {
          handleGetNextPageData(1, false);
          push({
            pathname: location.pathname,
            search: '',
          });
        }
      }
    } else {
      hasMountedRef.current = true;
    }

    previousTargetsRef.current = targets;
  }, [
    targets,
    location,
    handleBrowse,
    handleGetSuggestionForResearcher,
    handleChange,
    handleGetNextPageData,
    push,
  ]);

  const controlClassName = classNames(style.controls, {
    [style.controlsHidden]: !targets.get('showTabControls'),
  });

  const params = {
    requestParams: buildRequestParam(targets),
    stateParams: buildFiltersStateParam(targets),
  };

  const hasParams = !isEmpty(params.requestParams);

  return (
    <div className="tab-pane active" id="browse-targets" role="tabpanel">
      {hasPageAccess && (
        <div className={style.inner}>
          <BrowseTable
            columnDefs={columnDefs}
            data={targets}
            hasParams={hasParams}
            location={location}
            name="targets"
            onAbortRequest={handleRequestAbort}
            onAddTag={handleTargetsAddTag}
            onChange={handleChange}
            onClearFilter={handleFilterClear}
            onConfirmMerging={handleConfirmMerging}
            onDelete={handleTargetDelete}
            onDeleteConfirmation={handleTargetDeleteConfirmation}
            onDoubleClickRow={handleDoubleClickRow}
            onDownloadExport={handleDownloadExport}
            onGetNextPageData={handleGetNextPageData}
            onGetPageData={handleGetNextPageData}
            onGridReady={handleGridReady}
            onPushNextActionsDates={handleTargetPushNextActionsDates}
            onSaveAddUpdateBuyer={handleSaveAddUpdateBuyer}
            onSearchParamsBeforeBrowse={handleSearchParamsBeforeBrowse}
            onSelectRows={massSelectRows}
            onSetNextActions={handleTargetSetNextActions}
            onToggle={handleToggle}
            onUpdateQueryResult={handleUpdateQueryResult}
            requestParams={params.requestParams}
            stateParams={params.stateParams}
            user={user}
            {...rest}
          />
          <Card className={controlClassName} title="Filters">
            <BrowseStatusesFilter
              filter={targetsFilter.get('currentStatus')}
              info={targets.get('currentStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseStatusesFilter
              filter={targetsFilter.get('highStatus')}
              info={targets.get('highStatus')}
              onChange={handleChange}
              statuses={statuses}
            />
            <BrowseInputRange
              filter={targetsFilter.get('revenue')}
              info={targets.get('revenue')}
              onChange={handleChange}
            />
            <BrowseInputRange
              filter={targetsFilter.get('ebitda')}
              info={targets.get('ebitda')}
              onChange={handleChange}
            />
            <BrowseEmployeeInputRange
              filter={targetsFilter.get('employees')}
              info={targets.get('employees')}
              onChange={handleChange}
            />
            <BrowseStateFilter
              filter={targetsFilter.get('states')}
              info={targets.get('states')}
              onChange={handleChange}
              onGetCities={handleGetCompanyCitySuggestion}
              onGetStates={() => handleGetCompanyStateSuggestion('targets', 'target')}
              onStateFilterChange={handleStateFilterChange}
            />
            <BrowseCountryFilter
              allCountriesSuggestions={targets.get('allCountriesSuggestions')}
              continents={targets.getIn(['continent', 'suggestions'])}
              filter={targetsFilter.get('country')}
              info={targets.get('country')}
              onChange={handleChange}
              onGetCountriesByContinent={handleGetCountriesByContinent}
              type="targets"
            />
            <BrowseAutoCompleteTag
              filter={targetsFilter.get('module')}
              info={targets.get('module')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForModule}
              onGetSuggestion={handleGetSuggestionForModule}
            />
            <BrowseAutoCompleteTag
              filter={targetsFilter.get('analyst')}
              info={targets.get('analyst')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForAnalyst}
              onGetSuggestion={handleGetSuggestionForAnalyst}
            />
            <BrowseAutoCompleteTag
              filter={targetsFilter.get('dealMaker2')}
              info={targets.get('dealMaker2')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForDealMaker}
              onGetSuggestion={handleGetSuggestionForDealMaker}
            />
            <BrowseAutoCompleteTag
              filter={targetsFilter.get('researcher')}
              info={targets.get('researcher')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForResearcher}
              onGetSuggestion={handleGetSuggestionForResearcher}
            />
            <BrowseBuyerFilter
              filter={targetsFilter.get('buyer')}
              info={targets.get('buyer')}
              onChange={handleChange}
              onGetApprovalSuggestion={handleGetApprovalSuggestionForBuyer}
              onGetBuyerSuggestion={handleGetBuyerSuggestionForBuyer}
              onGetNextApprovalSuggestion={handleGetNextApprovalSuggestionForBuyer}
              onGetNextBuyerSuggestion={handleGetNextBuyerSuggestionForBuyer}
              onGetNextProjectSuggestion={handleGetNextProjectSuggestionForBuyer}
              onGetPrioritySuggestion={handleGetPrioritySuggestionForBuyer}
              onGetProjectSuggestion={handleGetProjectSuggestionForBuyer}
              onSubFilterChange={handleSubFilterChange}
              rangeFilterData={statuses}
            />
            <BrowseInputTag
              filter={targetsFilter.get('harvcoTags')}
              info={targets.get('harvcoTags')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseInputTag
              filter={targetsFilter.get('industry')}
              info={targets.get('industry')}
              onChange={handleChange}
              isBIC
              submitOnEnter
            />
            <BrowseActionFilter
              filter={targetsFilter.get('nextAction')}
              info={targets.get('nextAction')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForNextAction}
            />
            <BrowseActivityFilter
              buyersFilter={targetsFilter.getIn(['lastAction', 'buyers'])}
              buyersInfo={targets.getIn(['lastAction', 'buyers'])}
              filter={targetsFilter.get('lastAction')}
              info={targets.get('lastAction')}
              onChange={handleChange}
              onGetActivityUserSuggestion={handleGetSuggestionForActivityUser}
              onGetBuyerSuggestion={handleGetSuggestionForActivityBuyer}
              onGetNextActivityUserSuggestion={handleGetNextSuggestionForActivityUser}
              onGetNextBuyerSuggestion={handleGetNextSuggestionForActivityBuyer}
              onGetSuggestion={handleGetSuggestionForLastAction}
              onSubActivityFilterChange={handleSubActivityFilterChange}
              projectTypeFilter={targetsFilter.get('projectType')}
              projectTypeInfo={targets.getIn(['lastAction', 'projectType'])}
              statuses={statuses}
              usersFilter={targetsFilter.getIn(['lastAction', 'users'])}
              usersInfo={targets.getIn(['lastAction', 'users'])}
            />
            <BrowseInputTag
              filter={targetsFilter.get('description')}
              info={targets.get('description')}
              onChange={handleChange}
              shouldGroupTags
              submitOnEnter
            />
            <BrowseAutoCompleteTag
              filter={targetsFilter.get('companyType')}
              info={targets.get('companyType')}
              onChange={handleChange}
              onGetSuggestion={handleGetSuggestionForCompanyType}
            />
            <BrowseInputTag
              filter={targetsFilter.get('areaCode')}
              info={targets.get('areaCode')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseInputTag
              filter={targetsFilter.get('keywords')}
              info={targets.get('keywords')}
              onChange={handleChange}
              shouldGroupTags
              submitOnEnter
            />
            <BrowseDropDown
              filter={targetsFilter.get('projectType')}
              info={targets.get('projectType')}
              onChange={handleChange}
            />
            <BrowseUploadSearch
              clearStatus={!params.requestParams.upload_search}
              filter={targetsFilter.get('uploadSearch')}
              info={targets.get('uploadSearch')}
              onChange={handleChange}
              submitOnEnter
            />
            <BrowseCheckbox
              filter={targetsFilter.get('mergeResultOnly')}
              info={targets.get('mergeResultOnly')}
              onChange={handleChange}
            />
            {targets.getIn(['directorDashboard', 'reportType']) && (
              <DirectorDashboardFilter info={targets.get('directorDashboard')} onChange={handleChange} />
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

BrowseTargets.propTypes = {
  checkExportAvailability: PropTypes.func.isRequired,
  handleAbortRequest: PropTypes.func.isRequired,
  handleBrowse: PropTypes.func.isRequired,
  handleClearFilter: PropTypes.func.isRequired,
  handleGetBuyerApprovalSuggestion: PropTypes.func.isRequired,
  handleGetBuyerNameSuggestion: PropTypes.func.isRequired,
  handleGetBuyerProjectSuggestion: PropTypes.func.isRequired,
  handleGetBuyerSuggestion: PropTypes.func.isRequired,
  handleGetCitySuggestion: PropTypes.func.isRequired,
  handleGetCompanyStateSuggestion: PropTypes.func.isRequired,
  handleGetModuleSuggestion: PropTypes.func.isRequired,
  handleGetUsersSuggestion: PropTypes.func.isRequired,
  handleSearchParamsBeforeBrowse: PropTypes.func.isRequired,
  handleUpdateFilter: PropTypes.func.isRequired,
  handleUpdateTargetsActivityFilter: PropTypes.func.isRequired,
  handleUpdateTargetsBuyerFilter: PropTypes.func.isRequired,
  handleUpdateTargetsStateFilter: PropTypes.func.isRequired,
  updateTabVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  targets: state.browse.get('targets'),
  statuses: state.statuses.get('targetStatuses'),
  user: state.auth.get('user'),
  activities: state.eventActivities.getIn(['activities', 'all', 'target']),
  allActivitiesWithoutDuplicates: state.eventActivities.get('allActivitiesWithoutDuplicates'),
});

const mapDispatchToProps = dispatch => ({
  onFetchStatuses: () => dispatch(fetchTargetStatuses()),
  onFetchEventActivities: () => dispatch(fetchEventActivities()),
  ...bindActionCreators(browseActionCreators, dispatch),
  push,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(withRouter(memo(BrowseTargets)));
